import { getFirestore, collection, query, getDocs, where, doc, getDoc } from "firebase/firestore";
import { app } from "../firebaseFunctions/config";

const db = getFirestore(app);

export const eventDiscoveryService = {
  async getMPSProfile(userId) {
    try {
      const docRef = doc(db, "MPS_BusinessProfiles", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
      return null;
    } catch (error) {
      console.error("Error fetching MPS profile:", error);
      return null;
    }
  },

  // Helper to convert Firestore Timestamp or date string to Date object
  parseDate(value) {
    if (!value) return null;
    if (value.toDate && typeof value.toDate === 'function') {
      return value.toDate();
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  },

  async getPublishedEvents() {
    try {
      const eventsRef = collection(db, "events");
      const q = query(eventsRef, where("isDraft", "==", false));
      const querySnapshot = await getDocs(q);

      const events = [];
      querySnapshot.forEach((doc) => {
        const eventData = doc.data();
        events.push({
          id: doc.id,
          ...eventData,
          name: eventData.eventName,
          image: eventData.eventCoverPhoto,
          date: this.parseDate(eventData.startDate),
          location: eventData.location || '',
          city: eventData.city || '',
          state: eventData.state || '',
          applicationDeadline: this.parseDate(eventData.deadlineDate),
          sellerCapacity: eventData.sellerCapacity,
          sellerTypes: eventData.sellerTypes || [],
          vendorRegistrationFee: eventData.registrationFee ?? eventData.vendorRegistrationFee,
        });
      });

      events.sort((a, b) => {
        const dateA = a.date?.getTime() || 0;
        const dateB = b.date?.getTime() || 0;
        return dateA - dateB;
      });

      return events;
    } catch (error) {
      console.error("Error fetching published events:", error);
      return [];
    }
  },

  async getMPSApplicationStatuses(userId) {
    try {
      const eventRegistrationRef = collection(db, "mpsEventRegistration");
      const userQuery = query(eventRegistrationRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(userQuery);
      
      const statuses = {};
      querySnapshot.docs.forEach((doc) => {
        const data = doc.data();
        statuses[data.eventId] = {
          applicationStatus: data.applicationStatus,
          paymentStatus: data.paymentStatus,
          applicationId: doc.id,
        };
      });
      
      return statuses;
    } catch (error) {
      console.error("Error fetching application statuses:", error);
      return {};
    }
  },

  normalizeText(text) {
    if (!text) return [];
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2)
      .map(word => {
        if (word.endsWith('ers')) return word.slice(0, -3);
        if (word.endsWith('ors')) return word.slice(0, -3);
        if (word.endsWith('ies')) return word.slice(0, -3) + 'y';
        if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
        return word;
      });
  },

  categoryMap: {
    'food': ['food', 'cuisine', 'culinary', 'catering', 'restaurant', 'chef', 'kitchen', 'meal', 'dish', 'eat', 'beverage', 'drink'],
    'craft': ['craft', 'handmade', 'artisan', 'art', 'creative', 'handcraft', 'diy', 'maker'],
    'retail': ['retail', 'shop', 'store', 'merchandise', 'product', 'good', 'sell', 'vendor'],
    'service': ['service', 'provider', 'consultant', 'professional', 'specialist'],
    'jewelry': ['jewelry', 'jewel', 'accessory', 'accessories', 'necklace', 'ring', 'bracelet'],
    'clothing': ['clothing', 'apparel', 'fashion', 'wear', 'clothes', 'textile', 'fabric'],
    'health': ['health', 'wellness', 'organic', 'natural', 'holistic', 'fitness'],
    'farm': ['farm', 'produce', 'agriculture', 'farmer', 'fresh', 'local', 'vegetable', 'fruit'],
    'bakery': ['bakery', 'baked', 'baker', 'bread', 'pastry', 'dessert', 'cake', 'cookie'],
  },

  getCategories(tokens) {
    const matchedCategories = new Set();
    for (const token of tokens) {
      for (const [category, keywords] of Object.entries(this.categoryMap)) {
        if (keywords.some(kw => kw.includes(token) || token.includes(kw))) {
          matchedCategories.add(category);
        }
      }
    }
    return [...matchedCategories];
  },

  calculateFitScore(event, mpsProfile) {
    if (!mpsProfile || !event.sellerTypes || event.sellerTypes.length === 0) {
      return { score: 0, isGoodFit: false, matchedTypes: [] };
    }

    const mpsBusinessType = mpsProfile.typeOfBusiness || mpsProfile.businessType || '';
    const mpsBusinessName = mpsProfile.businessName || '';
    const mpsDescription = mpsProfile.description || mpsProfile.businessDescription || '';
    
    const mpsTokens = [
      ...this.normalizeText(mpsBusinessType),
      ...this.normalizeText(mpsBusinessName),
      ...this.normalizeText(mpsDescription),
    ];
    const mpsCategories = this.getCategories(mpsTokens);

    const matchedTypes = [];
    let score = 0;

    for (const sellerType of event.sellerTypes) {
      const sellerTokens = this.normalizeText(sellerType);
      const sellerCategories = this.getCategories(sellerTokens);
      
      const exactMatch = sellerTokens.some(st => mpsTokens.includes(st));
      if (exactMatch) {
        score += 100;
        matchedTypes.push(sellerType);
        continue;
      }

      const categoryMatch = sellerCategories.some(sc => mpsCategories.includes(sc));
      if (categoryMatch) {
        score += 75;
        matchedTypes.push(sellerType);
        continue;
      }

      for (const sellerToken of sellerTokens) {
        for (const mpsToken of mpsTokens) {
          if (sellerToken.includes(mpsToken) || mpsToken.includes(sellerToken)) {
            score += 30;
            matchedTypes.push(sellerType);
            break;
          }
        }
      }
    }

    return {
      score,
      isGoodFit: score >= 30,
      matchedTypes: [...new Set(matchedTypes)],
    };
  },

  async discoverEvents(userId) {
    try {
      const [mpsProfile, allEvents, applicationStatuses] = await Promise.all([
        this.getMPSProfile(userId),
        this.getPublishedEvents(),
        this.getMPSApplicationStatuses(userId),
      ]);

      const now = new Date();
      const upcomingEvents = allEvents.filter(event => {
        if (!event.date) return true;
        return event.date >= now;
      });

      const eventsWithScores = upcomingEvents.map(event => {
        const fitInfo = this.calculateFitScore(event, mpsProfile);
        const applicationInfo = applicationStatuses[event.id] || {};
        
        return {
          ...event,
          fitScore: fitInfo.score,
          isGoodFit: fitInfo.isGoodFit,
          matchedTypes: fitInfo.matchedTypes,
          status: applicationInfo.applicationStatus || "Not Applied",
          paymentStatus: applicationInfo.paymentStatus || null,
          applicationId: applicationInfo.applicationId || null,
        };
      });

      eventsWithScores.sort((a, b) => {
        const dateA = a.date?.getTime() || Infinity;
        const dateB = b.date?.getTime() || Infinity;
        return dateA - dateB;
      });

      const goodFitEvents = eventsWithScores.filter(e => e.isGoodFit);
      const otherEvents = eventsWithScores.filter(e => !e.isGoodFit);

      return {
        success: true,
        goodFitEvents,
        otherEvents,
        allEvents: eventsWithScores,
        mpsProfile,
      };
    } catch (error) {
      console.error("Error discovering events:", error);
      return {
        success: false,
        goodFitEvents: [],
        otherEvents: [],
        allEvents: [],
        mpsProfile: null,
        error: error.message,
      };
    }
  },

  async getPastEventHistory(userId) {
    try {
      const [allEvents, appStatuses] = await Promise.all([
        this.getPublishedEvents(),
        this.getMPSApplicationStatusesFull(userId),
      ]);

      const now = new Date();
      const pastEvents = allEvents
        .filter(event => event.date && event.date < now && appStatuses[event.id])
        .map(event => {
          const app = appStatuses[event.id];
          return {
            ...event,
            status: app.applicationStatus,
            paymentStatus: app.paymentStatus,
            applicationId: app.applicationId,
            boothNumber: app.boothNumber || null,
            registrationFee: app.registrationFee ?? event.registrationFee,
          };
        })
        .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));

      return { success: true, pastEvents };
    } catch (error) {
      console.error("Error fetching past event history:", error);
      return { success: false, pastEvents: [] };
    }
  },

  async getMPSApplicationStatusesFull(userId) {
    try {
      const eventRegistrationRef = collection(db, "mpsEventRegistration");
      const userQuery = query(eventRegistrationRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(userQuery);

      const statuses = {};
      querySnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        statuses[data.eventId] = {
          applicationStatus: data.applicationStatus,
          paymentStatus: data.paymentStatus,
          applicationId: docSnap.id,
          boothNumber: data.boothNumber || null,
          registrationFee: data.registrationFee ?? null,
        };
      });

      return statuses;
    } catch (error) {
      console.error("Error fetching full application statuses:", error);
      return {};
    }
  },

  filterEvents(events, filters = {}) {
    let filtered = [...events];

    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(event => 
        event.sellerTypes?.some(type => 
          type.toLowerCase().includes(filters.category.toLowerCase())
        )
      );
    }

    if (filters.location) {
      const searchLocation = filters.location.toLowerCase();
      filtered = filtered.filter(event => 
        (event.location || '').toLowerCase().includes(searchLocation) ||
        (event.city || '').toLowerCase().includes(searchLocation) ||
        (event.state || '').toLowerCase().includes(searchLocation)
      );
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(event =>
        (event.name || '').toLowerCase().includes(query) ||
        (event.description || '').toLowerCase().includes(query) ||
        (event.location || '').toLowerCase().includes(query)
      );
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(event => event.date && event.date >= fromDate);
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filtered = filtered.filter(event => event.date && event.date <= toDate);
    }

    if (filters.showGoodFitOnly) {
      filtered = filtered.filter(event => event.isGoodFit);
    }

    if (filters.hideApplied) {
      filtered = filtered.filter(event => event.status === "Not Applied");
    }

    return filtered;
  },
};
